import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, viewChildren } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { first, uniqBy } from "lodash";
import { IParty } from "rl-common/components/contacts/models/i-party";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { SearchFieldNames } from "rl-common/components/entities/entity-search/query.models";
import { NumberInputComponent } from "rl-common/components/number-input/number-input.component";
import { TextAreaComponent } from "rl-common/components/text/text-area/text-area.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { TypeaheadInputComponent } from "rl-common/components/typeahead-input/typeahead-input.component";
import { ITypeaheadPage } from "rl-common/components/typeahead-input/typeahead-input.models";
import { ICharacteristicType } from "rl-common/models/i-characteristic-type";
import { IEntitySearchDoc } from "rl-common/models/i-entity-search-doc";
import { IQueryNode } from "rl-common/models/i-query-node";
import { CharTypeId, ConstUtils } from "rl-common/rl-common.consts";
import { IWfRole } from "rl-common/services/company/company.models";
import { CompanyService } from "rl-common/services/company/company.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { SearchService } from "rl-common/services/search/search.service";
import { TaskAssignmentLogic, TaskAssignmentRule, TaskConfig, TaskControlGroup } from "rl-common/services/task-config/task-config.models";
import { QueryUtil } from "rl-common/utils/query.util";
import { Observable, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { IKeyValueOption } from "../../workflow-details-actions/workflow-action-edit/workflow-action-edit.models";
import { TaskConditionBuilderComponent } from "../task-condition-builder/task-condition-builder.component";
import { AssigneeType, DueDateUnits, ITaskAssignee, ITaskConfigurationAssignmentForm, ITaskConfigurationGroupForm, TaskSequence } from "./workflow-task-group-form.models";

@Component({
	selector: "rl-workflow-task-group-form",
	imports: [TextInputComponent, TextAreaComponent, DropdownSingleComponent, NumberInputComponent, FormsModule, ReactiveFormsModule, TypeaheadInputComponent, TaskConditionBuilderComponent, NgbPopover],
	templateUrl: "./workflow-task-group-form.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./workflow-task-group-form.component.scss",
})
export class WorkflowTaskGroupFormComponent implements OnDestroy {

	task = input.required<TaskConfig>();
	taskGroup = input<TaskControlGroup | null>(null);
	charTypeId = input.required<number>();
	processId = input.required<number>();

	private readonly _formBuilder = inject(FormBuilder);
	private readonly _companyService = inject(CompanyService);
	private readonly _searchService = inject(SearchService);
	private readonly _oneConfig = inject(OneConfigService);

	form: FormGroup<ITaskConfigurationGroupForm>;

	displayConditions = false;

	assigneeTypeOptions: DropdownOptions<IKeyValueOption> = {
		items: [
			{ key: AssigneeType.WorkflowRole, value: "Workflow Role" },
			{ key: AssigneeType.Users, value: "Users" },
			{ key: AssigneeType.Party, value: "Party" }
		],
		rowKeyFn: (type: IKeyValueOption) => type.key,
		rowLabelFn: (type: IKeyValueOption) => type.value
	};
	assigneeType = AssigneeType;

	sequenceOptions: DropdownOptions<IKeyValueOption> = {
		items: [
			{ key: TaskSequence.Any, value: "Any" }
		],
		rowKeyFn: (type: IKeyValueOption) => type.key,
		rowLabelFn: (type: IKeyValueOption) => type.value
	};

	dueDateIncrementsOptions: DropdownOptions<IKeyValueOption> = {
		items: [
			{ key: DueDateUnits.Days, value: "Days" },
			{ key: DueDateUnits.BusinessDays, value: "Business Days" },
			{ key: DueDateUnits.Weeks, value: "Weeks" },
			{ key: DueDateUnits.Months, value: "Months" },
			{ key: DueDateUnits.NoDueDate, value: "No Due Date" }
		],
		rowKeyFn: (type: IKeyValueOption) => type.key,
		rowLabelFn: (type: IKeyValueOption) => type.value
	};

	rolesResponse = toSignal(this._companyService.getWfRoles(1, 100, ""));
	roleOptions = computed<DropdownOptions<IWfRole>>(() => {
		const roles = this.rolesResponse()?.data ?? [];
		return {
			items: roles,
			rowKeyFn: (x) => x.roleID,
			rowLabelFn: (x) => x.roleName
		};
	});

	charTypeOptions = computed<DropdownOptions<ICharacteristicType>>(() => {
		const charTypes = this._oneConfig.getCharTypes()
			.filter(ct => ConstUtils.isModuleLevelCharType(ct.charTypeID) && ct.charTypeID !== CharTypeId.Mock);
		return {
			items: charTypes,
			rowKeyFn: ct => ct.charTypeID,
			rowLabelFn: ct => ct.charTypeName
		};
	});

	getPartyOptionsForCharType(charTypeID: number | null | undefined): DropdownOptions<IParty> {
		const parties = charTypeID
			? uniqBy(
				this._oneConfig.getTemplates(charTypeID)
					.flatMap(t => this._oneConfig.getParties(charTypeID, t.templateID)),
				p => p.partyID
			)
			: [];
		return {
			items: parties,
			rowKeyFn: p => p.partyID,
			rowLabelFn: p => p.partyName
		};
	}

	userSearchFn = (term: string, page: number, pageSize: number): Observable<ITypeaheadPage<IEntitySearchDoc>> => {
		return this._searchService.search(CharTypeId.User, term, {}, {
			rows: pageSize,
			start: page * pageSize,
			sortField: "score",
			fields: [],
			facetFields: [],
			filterQueries: [QueryUtil.$eq(SearchFieldNames.Entity.isActive, true)]
		}).pipe(map(r => ({ items: r.documents, total: r.numFound })));
	};

	userLabelFn = (user: IEntitySearchDoc): string => user.title;
	userKeyFn = (user: IEntitySearchDoc): number => user.recordID;

	private readonly _overridePopovers = viewChildren<NgbPopover>("overridePopover");

	private _assignmentSub: Subscription | null = null;
	private _assigneeSubs: Subscription[] = [];
	private _openCheckedOverridePopovers = false;

	get isValid() {
		return this.form?.valid;
	}

	get isDirty() {
		return this.form?.dirty;
	}

	markAllAsTouched() {
		this.form?.markAllAsTouched();
	}

	constructor() {
		effect(() => {
			const taskGroup = this.taskGroup();
			this.buildForm(taskGroup);
		});

		// popovers only exist once the assignment rows render, so open the pre-checked ones here
		effect(() => {
			const popovers = this._overridePopovers();
			if (!this._openCheckedOverridePopovers || popovers.length === 0) {
				return;
			}
			this._openCheckedOverridePopovers = false;
			const assignments = this.form.controls.assignments.controls;
			popovers.forEach((popover, index) => {
				this.syncOverridePopover(popover, assignments[index]?.controls.overrideCompletions.value === true);
			});
		});
	}

	syncOverridePopover(popover: NgbPopover, checked: boolean) {
		if (checked) {
			popover.open();
		} else {
			popover.close();
		}
	}

	conditionsToggled() {
		if (!this.displayConditions) {
			this.form.controls.queryNode.setValue(null);
			this.form.markAsDirty();
		}
	}

	onQueryNodeChange(queryNode: IQueryNode | null) {
		this.form.controls.queryNode.setValue(queryNode);
		this.form.markAsDirty();
	}

	addAssignment(markAsDirty: boolean = true) {
		const formArray = this.form.controls.assignments;
		formArray.push(this.buildAssignmentFormGroup(null));
		if (markAsDirty) {
			this.form.markAsDirty();
		}
	}

	deleteAssignment(index: number, markAsDirty: boolean = true) {
		const formArray = this.form.controls.assignments;
		formArray.removeAt(index);
		if (markAsDirty) {
			this.form.markAsDirty();
		}
	}

	buildForm(taskGroup: TaskControlGroup | null) {
		this._assignmentSub?.unsubscribe();
		this._assigneeSubs.forEach(s => s.unsubscribe());
		this._assigneeSubs = [];

		const items = this.buildSequenceItems(taskGroup?.assignments?.length ?? 0);
		this.sequenceOptions.items = items;

		const assignmentFormGroups = (taskGroup?.assignments ?? []).map(a => this.buildAssignmentFormGroup(a));

		this.form = this._formBuilder.group<ITaskConfigurationGroupForm>({
			queryNode: new FormControl<IQueryNode | null>(taskGroup?.queryNode ?? null),
			assignments: new FormArray<FormGroup<ITaskConfigurationAssignmentForm>>(assignmentFormGroups, [this.minArrayLength(1)])
		});

		this._assignmentSub = this.form.controls.assignments.valueChanges.pipe(
			map(assignments => assignments.length),
			distinctUntilChanged()
		).subscribe((count) => {
			const items = this.buildSequenceItems(count);
			this.sequenceOptions.items = items;
		});

		// Add one assignment if none exist
		if (this.form.controls.assignments.length === 0) {
			this.addAssignment(false);
		}

		this.displayConditions = taskGroup?.queryNode != null;
		this._openCheckedOverridePopovers = true;
	}

	private buildSequenceItems(count: number) {
		const items: IKeyValueOption[] = [];
		for (let i = 0; i < count; i++) {
			const sequence = i + 1;
			items.push({
				key: sequence,
				value: `${sequence}`
			});
		}
		return [
			{ key: TaskSequence.Any, value: "Any" },
			...items
		];
	}

	buildAssignmentFormGroup(assignment: TaskAssignmentRule | null): FormGroup<ITaskConfigurationAssignmentForm> {
		const sequence = assignment?.taskSequence != null
			? this.sequenceOptions.items.find(o => o.key === assignment.taskSequence)
			: first(this.sequenceOptions.items);

		const dueDateIncrements = assignment?.dueDateUnits != null
			? this.dueDateIncrementsOptions.items.find(o => o.key === assignment.dueDateUnits)
			: this.dueDateIncrementsOptions.items.find(o => o.key === DueDateUnits.NoDueDate);

		const assigneeFormGroups = assignment?.assignmentDetails.map(x => this.buildAssigneeFormGroup(x)) ?? [this.buildAssigneeFormGroup(null)];

		return this._formBuilder.group<ITaskConfigurationAssignmentForm>({
			id: new FormControl<string>(uuidv4()),
			taskName: new FormControl<string | null>(assignment?.taskAssignmentName ?? null, { validators: [Validators.required] }),
			description: new FormControl<string | null>(assignment?.description ?? null),
			assignees: new FormArray<FormGroup<ITaskAssignee>>(assigneeFormGroups, [this.minArrayLength(1)]),
			requiredCompletions: new FormControl<number | null>(assignment?.requiredCompletions ?? 1, [Validators.min(1)]),
			overrideCompletions: new FormControl<boolean>(assignment?.overrideCompletions ?? false),
			sequence: new FormControl<IKeyValueOption | null>(sequence ?? null),
			dueDateQuantity: new FormControl<number | null>(assignment?.dueDate ?? 0, [Validators.min(0)]),
			dueDateIncrements: new FormControl<IKeyValueOption | null>(dueDateIncrements ?? null)
		});
	}

	buildAssigneeFormGroup(assignee: TaskAssignmentLogic | null): FormGroup<ITaskAssignee> {
		const assigneeTypes = this.assigneeTypeOptions.items;
		const assigneeType = assigneeTypes.find(x => x.key === assignee?.taskAssignAs) ?? first(assigneeTypes) ?? null;
		const assigneeId = assignee?.taskAssignTo ?? null;

		let assigneeUser: IEntitySearchDoc | null = null;
		let assigneeRole: IWfRole | null = null;
		let assigneeCharType: ICharacteristicType | null = null;
		let assigneeParty: IParty | null = null;
		if (assigneeId && assigneeType?.key === AssigneeType.Users) {
			const userTitle = assignee?.taskAssignToName ?? `User ${assigneeId}`;
			assigneeUser = { recordID: assigneeId, title: userTitle } as IEntitySearchDoc;
		} else if (assigneeType?.key === AssigneeType.Party && assigneeId) {
			const charTypeID = assignee?.assignPartyCharTypeId ?? null;
			if (charTypeID) {
				assigneeCharType = this._oneConfig.getCharTypes()
					.find(ct => ct.charTypeID === charTypeID) ?? null;
				assigneeParty = this._oneConfig.getDivParties()
					.find(p => p.partyID === assigneeId && p.charTypeID === charTypeID)
					?? { partyID: assigneeId, partyName: assignee?.taskAssignToName ?? `Party ${assigneeId}`, charTypeID } as IParty;
			}
		} else if (assigneeId) {
			const roles = this.roleOptions().items ?? [];
			assigneeRole = roles.find(r => r.roleID === assigneeId) ?? null;
		}

		const formGroup = this._formBuilder.group<ITaskAssignee>({
			assigneeType: new FormControl<IKeyValueOption | null>(assigneeType),
			assigneeId: new FormControl<number | null>(assigneeId),
			assigneeUser: new FormControl<IEntitySearchDoc | null>(assigneeUser),
			assigneeRole: new FormControl<IWfRole | null>(assigneeRole),
			assigneeCharType: new FormControl<ICharacteristicType | null>(assigneeCharType),
			assigneeParty: new FormControl<IParty | null>(assigneeParty)
		}, { validators: [this.assigneeSelectionValidator()] });

		const charTypeSub = formGroup.controls.assigneeCharType.valueChanges.subscribe(() => {
			formGroup.controls.assigneeParty.setValue(null);
		});
		this._assigneeSubs.push(charTypeSub);

		const partyDisableSub = formGroup.controls.assigneeCharType.valueChanges.subscribe((charType) => {
			if (charType) {
				formGroup.controls.assigneeParty.enable();
			} else {
				formGroup.controls.assigneeParty.disable();
			}
		});
		this._assigneeSubs.push(partyDisableSub);

		if (!assigneeCharType) {
			formGroup.controls.assigneeParty.disable();
		}

		return formGroup;
	}

	addAssignee(formGroup: FormGroup<ITaskConfigurationAssignmentForm>, markAsDirty: boolean = true) {
		formGroup.controls.assignees.push(this.buildAssigneeFormGroup(null));
		if (markAsDirty) {
			formGroup.markAsDirty();
		}
	}

	deleteAssignee(formGroup: FormGroup<ITaskConfigurationAssignmentForm>, index: number, markAsDirty: boolean = true) {
		formGroup.controls.assignees.removeAt(index);
		if (markAsDirty) {
			formGroup.markAsDirty();
		}
	}

	buildTaskControlGroup(): TaskControlGroup {
		const formValue = this.form.getRawValue();
		const existing = this.taskGroup();

		const assignments: TaskAssignmentRule[] = formValue.assignments.map((assignment, i) => {
			const existingAssignment = existing?.assignments?.[i];

			const assignmentDetails: TaskAssignmentLogic[] = assignment.assignees.map((assignee, j) => {
				const existingDetail = existingAssignment?.assignmentDetails?.[j];
				const isUser = assignee.assigneeType?.key === AssigneeType.Users;
				const isParty = assignee.assigneeType?.key === AssigneeType.Party;
				const taskAssignTo = isUser ? (assignee.assigneeUser?.recordID ?? null) : isParty ? (assignee.assigneeParty?.partyID ?? null) : (assignee.assigneeRole?.roleID ?? null);
				const taskAssignToName = isUser ? (assignee.assigneeUser?.title ?? null) : isParty ? (assignee.assigneeParty?.partyName ?? null) : null;
				const assignPartyCharTypeId = isParty ? (assignee.assigneeCharType?.charTypeID ?? null) : null;

				return {
					id: existingDetail?.id ?? 0,
					taskCreationLogicId: existingDetail?.taskCreationLogicId ?? 0,
					divId: existingDetail?.divId ?? 0,
					taskAssignAs: assignee.assigneeType?.key ?? null,
					taskAssignTo: taskAssignTo,
					taskAssignToName: taskAssignToName ?? "",
					taskManager: existingDetail?.taskManager ?? 0,
					assignPartyCharTypeId: assignPartyCharTypeId
				} as TaskAssignmentLogic;
			});

			return {
				id: existingAssignment?.id ?? 0,
				taskControlGroupId: existingAssignment?.taskControlGroupId ?? 0,
				taskAssignmentName: assignment.taskName,
				description: assignment.description,
				requiredCompletions: assignment.requiredCompletions,
				overrideCompletions: assignment.overrideCompletions,
				taskSequence: assignment.sequence?.key ?? TaskSequence.Any,
				dueDate: assignment.dueDateIncrements?.key === DueDateUnits.NoDueDate ? null : assignment.dueDateQuantity,
				dueDateUnits: assignment.dueDateIncrements?.key === DueDateUnits.NoDueDate ? null : assignment.dueDateIncrements?.key ?? null,
				assignmentDetails
			} as TaskAssignmentRule;
		});

		return {
			id: existing?.id ?? 0,
			taskConfigId: this.taskGroup()?.taskConfigId ?? this.task().id,
			groupName: existing?.groupName ?? null,
			queryNode: formValue.queryNode,
			assignments
		} as TaskControlGroup;
	}

	private assigneeSelectionValidator() {
		return (control: AbstractControl): ValidationErrors | null => {
			const group = control as FormGroup<ITaskAssignee>;
			const type = group.controls.assigneeType.value?.key;
			if (type === AssigneeType.Users && !group.controls.assigneeUser.value) {
				return { assigneeRequired: { message: "A user must be selected." } };
			}
			if (type === AssigneeType.WorkflowRole && !group.controls.assigneeRole.value) {
				return { assigneeRequired: { message: "A workflow role must be selected." } };
			}
			if (type === AssigneeType.Party) {
				if (!group.controls.assigneeCharType.value) {
					return { assigneeRequired: { message: "A record type must be selected." } };
				}
				if (!group.controls.assigneeParty.value) {
					return { assigneeRequired: { message: "A party must be selected." } };
				}
			}
			return null;
		};
	}

	private minArrayLength(min: number) {
		return (control: AbstractControl): ValidationErrors | null => {
			const array = control as FormArray;
			return array.length >= min ? null : { minArrayLength: { required: min, actual: array.length } };
		};
	}

	ngOnDestroy(): void {
		this._assignmentSub?.unsubscribe();
		this._assigneeSubs.forEach(s => s.unsubscribe());
	}
}
